import { useVirtualizer, type VirtualItem } from '@tanstack/react-virtual';
import React, { useCallback, useRef, useState } from 'react';

export type VirtualListProps<T> = {
  itemHeight: number;
  height?: number;
  fixedHeight?: boolean;
  virtualContainerWidth?: string;
  items: T[];
  ItemTemplate: React.ForwardRefExoticComponent<
    {
      item: T;
      'data-index': number;
      style?: React.CSSProperties;
    } & React.RefAttributes<any>
  >;
};

export const VirtualList = React.memo(function VirtualList<T>({
  items,
  ItemTemplate,
  itemHeight,
  fixedHeight = true,
  virtualContainerWidth,
  height = 400
}: VirtualListProps<T>) {
  const [scrollElement, setScrollElement] = useState<HTMLDivElement | null>(null);
  // Callback ref
  const parentRef = useCallback((node: HTMLDivElement | null) => {
    if (node !== null) {
      setScrollElement(node);
    }
  }, []);
  const rowVirtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => scrollElement,
    estimateSize: () => itemHeight,
    overscan: 5,
    isScrollingResetDelay: 200
  });
  if (items.length < 10)
    return (
      <>
        {items.map((item, i) => (
          <ItemTemplate key={i} item={item} data-index={i} />
        ))}
      </>
    );
  return (
    <div
      ref={parentRef}
      className="overflow-y-auto "
      style={{ height, width: '100%' }}
      tabIndex={-1}
    >
      <div
        style={{
          height: `${rowVirtualizer.getTotalSize()}px`,
          position: 'relative',
          overflowX: 'hidden',
          overflowAnchor: 'none',
          overflowY: 'hidden',
          display: 'block',
          width: '100%',
          minWidth: virtualContainerWidth
        }}
      >
        {rowVirtualizer.getVirtualItems().map((virtualRow: VirtualItem) => (
          <ItemTemplate
            key={virtualRow.index}
            item={items[virtualRow.index]}
            data-index={virtualRow.index}
            ref={fixedHeight ? null : rowVirtualizer.measureElement}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              whiteSpace: 'nowrap',
              height: `${virtualRow.size}px`,
              transform: `translateY(${virtualRow.start}px)`
            }}
          />
        ))}
      </div>
    </div>
  );
});
